import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    IconButton,
    CircularProgress,
    InputAdornment,
    Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { changestaffpassword } from './BaseURL';

const PRIMARY_COLOR = '#1A9B7F';
const PRIMARY_DARK = '#157763';

const passwordFieldSx = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        '&:hover fieldset': { borderColor: PRIMARY_COLOR },
        '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR }
    },
    '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY_COLOR }
};

const PasswordField = ({ label, value, onChange, show, onToggle }) => (
    <TextField
        fullWidth
        label={label}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={passwordFieldSx}
        InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                    <Lock sx={{ color: PRIMARY_COLOR, fontSize: 20 }} />
                </InputAdornment>
            ),
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton onClick={onToggle} edge="end" size="small">
                        {show ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                </InputAdornment>
            )
        }}
    />
);

function ChangePasswordDialog({ open, userInfo, onClose }) {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const resetForm = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async () => {
        setError('');

        if (!currentPassword) {
            setError('Please enter your current password');
            return;
        }

        if (newPassword.length < 6) {
            setError('New password must be at least 6 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        if (currentPassword === newPassword) {
            setError('New password must be different from current password');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(changestaffpassword, {
                email: userInfo?.email,
                current_password: currentPassword,
                new_password: newPassword
            });

            if (response.data.success) {
                handleClose();
                Swal.fire({
                    title: 'Password Updated!',
                    text: 'Your password has been changed successfully.',
                    icon: 'success',
                    confirmButtonColor: PRIMARY_COLOR
                });
            } else {
                setError(response.data.message || 'Failed to update password');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: '12px', overflow: 'hidden' }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: PRIMARY_COLOR,
                color: 'white',
                py: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Lock />
                    <Typography variant="h6" fontWeight={600}>Change Password</Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enter your current password and choose a new password.
                </Typography>

                <PasswordField
                    label="Current Password"
                    value={currentPassword}
                    onChange={setCurrentPassword}
                    show={showCurrentPassword}
                    onToggle={() => setShowCurrentPassword(!showCurrentPassword)}
                />

                <Box sx={{ my: 1, borderTop: '1px solid', borderColor: 'divider' }} />

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, mt: 1.5 }}>
                    New password must be at least 6 characters
                </Typography>

                <PasswordField
                    label="New Password"
                    value={newPassword}
                    onChange={setNewPassword}
                    show={showNewPassword}
                    onToggle={() => setShowNewPassword(!showNewPassword)}
                />

                <PasswordField
                    label="Confirm New Password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    show={showConfirmPassword}
                    onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                />

                {error && (
                    <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                        {error}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                    onClick={handleClose}
                    sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    sx={{
                        bgcolor: PRIMARY_COLOR,
                        '&:hover': { bgcolor: PRIMARY_DARK },
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 3
                    }}
                >
                    {loading ? <CircularProgress size={22} color="inherit" /> : 'Update Password'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ChangePasswordDialog;